import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IMeterReadingRecord } from "../../types/meterReadingRecord.type";
import { api } from "../../api/api";
import dayjs from "dayjs";
import {
  Button,
  DatePicker,
  message,
  Popconfirm,
  Table,
  Typography,
  Form,
  Modal,
  InputNumber,
  Select,
  Input,
} from "antd";
import useGetRequest from "../../hooks/useGetRequest";
import { Cashbox } from "../../types/cashbox.type";
import { Machine } from "../../types/machine.type";
import { useExchangeRate } from "../../contexts/exchangeRate.context";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function MeterReadingRecords() {
  const { exchangeRate } = useExchangeRate();
  const { id, name } = useParams();
  const [records, setRecords] = useState<IMeterReadingRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { data: cashboxesList, loading: cashboxLoading } =
    useGetRequest(`/cashbox/list`);
  const { data: machineList, loading: machineListLoading } =
    useGetRequest("/machine/list");
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/meter-readings/records/${id}`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
        },
      });

      setRecords(response.data.records);
      setTotal(response.data.total);
    } catch (error: any) {
      console.error("Error fetching records:", error);
      message.error(error.response?.data?.error || "Ошибка при загрузке записей");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [page, pageSize, dateRange]);

  const handleSubmitForm = async (values: {
    amount: number;
    reading: number;
    cashboxId: string;
    machineId: string;
    description: string;
  }) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        meterReadingId: id,
        exchangeRate,
      };
      await api.post("/meter-readings/record", data);
      message.success("Запись успешно добавлена");
      fetchRecords();
      setIsModalOpen(false);
      form.resetFields();
    } catch (error: any) {
      console.error("Error creating records:", error);
      message.error(error.response?.data?.error || "Ошибка при добавлении записи");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDeleteRecord = async (recordId: string) => {
    setDeleteLoadingId(recordId);
    try {
      await api.delete(`/meter-readings/record/${recordId}`);
      message.success("Запись успешно удалена");
      fetchRecords();
    } catch (error: any) {
      console.error("Error deleting record:", error);
      message.error(error.response?.data?.error || "Ошибка при удалении записи");
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const columns = [
    {
      title: "Дата",
      key: "createdAt",
      dataIndex: "createdAt",
      render: (createdAt: Date) => dayjs(createdAt).format("DD.MM.YY HH:mm"),
    },
    {
      title: "Сумма",
      key: "amount",
      dataIndex: "amount",
    },
    {
      title: "Показание",
      key: "reading",
      dataIndex: "reading",
    },
    {
      title: "Описание",
      key: "description",
      dataIndex: "description",
    },
    {
      title: "Баланс до",
      key: "balanceBefore",
      dataIndex: "balanceBefore",
    },
    {
      title: "Баланс после",
      key: "balanceAfter",
      dataIndex: "balanceAfter",
    },
    {
      title: "Касса",
      key: "cashboxName",
      dataIndex: "cashboxName",
    },
    {
      title: "Машина",
      key: "machineName",
      dataIndex: "machineName",
    },
    {
      title: "Курс",
      key: "exchangeRate",
      dataIndex: "exchangeRate",
    },
    {
      title: "Валюта",
      key: "currency",
      dataIndex: "currency",
    },
    {
      title: "Создал",
      key: "ownerId",
      dataIndex: "ownerId",
      render: (ownerId: { username: string } | null) =>
        ownerId?.username ?? "—",
    },
    {
      title: "Действия",
      key: "actions",
      render: (_: unknown, record: IMeterReadingRecord) => (
        <Popconfirm
          title="Удалить запись?"
          description="Это действие нельзя отменить."
          onConfirm={() => handleDeleteRecord(record._id)}
          okText="Удалить"
          cancelText="Отмена"
        >
          <Button
            type="link"
            danger
            loading={deleteLoadingId === record._id}
            disabled={deleteLoadingId !== null && deleteLoadingId !== record._id}
          >
            Удалить
          </Button>
        </Popconfirm>
      ),
    },
  ];
  return (
    <div style={{ padding: 25 }}>
      <Title level={2}>Записи счетчика {name}</Title>
      <Button
        type="primary"
        style={{ marginBottom: 16, marginRight: 16 }}
        onClick={() => setIsModalOpen(true)}
      >
        Добавить
      </Button>
      <RangePicker
        onChange={handleRangeChange}
        placeholder={["Начало", "Конец"]}
        format="DD.MM.YY"
        value={dateRange}
      />

      <Table
        columns={columns}
        dataSource={records}
        loading={loading}
        rowKey="_id"
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSize(pageSize);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавить"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitForm}>
          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Введите сумму" }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="reading"
            label="Показание"
            rules={[{ required: true, message: "Введите показание" }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={cashboxLoading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Option key={cashbox._id} value={cashbox._id}>
                  {cashbox.name} ({cashbox.currency})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="machineId"
            label="Машина"
            rules={[{ required: true, message: "Выберите машину" }]}
          >
            <Select placeholder="Выберите машину" loading={machineListLoading}>
              {machineList?.map((machine: Machine) => (
                <Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание" }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
